import { Assert } from "../../Assert";
import { PartialNullable } from "../../Extensions";
import { ObjectHelper } from "../../Helpers/ObjectHelper";
import { StringHelper } from "../../Helpers/StringHelper";
import { $$, IJNativeEvent, JNative, JNativeStatic } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyBoolean, PropertyDecimal, PropertyInt32, PropertyString } from "../../RadObject";
import { ClientAwareCategoryAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareCategoryAttribute";
import { ClientAwareDisplayNameAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareDisplayNameAttribute";
import { SvgHelper } from "../SvgHelper";
import { Button } from "./Button";
import { IInputControl, InputControl, InputControlPropertyValues } from "./InputControl";
import { NAMESPACE } from "./_Namespace";

import "./TextBox.scss";

export enum TextBoxTypeEnum
{
    date = "date",
    email = "email",
    multiline = "multiline",
    normal = "normal",
    number = "number",
    number2Dp = "number2Dp",
    password = "password",
}

export type TextBoxPropertyValues =
    InputControlPropertyValues
    & {
        allowShowPassword: boolean;
        autoComplete: string;
        changeEvent: string;
        icon: string;
        inputMax: number;
        inputMin: number;
        inputSize: number;
        leftLabelText: string;
        maximumLength: number;
        placeholder: string;
        rightLabelText: string;
        showPassword: boolean;
        svgKey: string;
        type: TextBoxTypeEnum;
    };

export type TextBoxProperties = {
    [name in keyof TextBoxPropertyValues]: Property<TextBoxPropertyValues[name]>
};

export interface ITextBox extends IInputControl, TextBoxProperties
{
}

export class TextBox extends InputControl<TextBoxPropertyValues> implements ITextBox
{
    // #region Static

    public static override classNamespace = NAMESPACE + "TextBox";
    public static override className = "TextBox";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<TextBoxPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                autoComplete: "off",
                changeEvent: "change input keyup",
                type: TextBoxTypeEnum.normal
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected $$iconElement?: JNative<HTMLElement>;
    protected $$leftLabelElement?: JNative<HTMLElement>;
    protected $$rightLabelElement?: JNative<HTMLElement>;
    public $$valueElement?: JNative<HTMLInputElement | HTMLTextAreaElement>;
    protected buttonShowHidePassword?: Button;

    // #endregion

    // #region Properties

    public allowShowPassword!: PropertyBoolean;
    public autoComplete!: PropertyString;
    public changeEvent!: PropertyString;
    public icon!: PropertyString;
    public inputMax!: PropertyDecimal;
    public inputMin!: PropertyDecimal;
    public inputSize!: PropertyInt32;
    public leftLabelText!: PropertyString;
    public maximumLength!: PropertyInt32;
    public placeholder!: PropertyString;
    public rightLabelText!: PropertyString;
    public showPassword!: PropertyBoolean;
    public svgKey!: PropertyString;
    public type!: Property<TextBoxTypeEnum>;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<TextBoxPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-text-box ui form");

        // Trigger Property Changes
        this.allowShowPassword.triggerChange();
        this.autoComplete.triggerChange();
        this.icon.triggerChange();
        this.inputMax.triggerChange();
        this.inputMin.triggerChange();
        this.inputSize.triggerChange();
        this.leftLabelText.triggerChange();
        this.placeholder.triggerChange();
        this.rightLabelText.triggerChange();
        this.showPassword.triggerChange();
        this.svgKey.triggerChange();
        this.type.triggerChange();
    }

    // #endregion

    // #region Public Methods

    public select()
    {
        this.$$valueElement?.select();
    }

    // #endregion

    // #region Protected Methods

    protected _allowShowPasswordChanged(change: PropertyChange<boolean>)
    {
        const
            type = this.type.get();

        if (type === TextBoxTypeEnum.password && change.currentValue)
            this.addCssClass("rad-state-allow-show-password");
        else
            this.removeCssClass("rad-state-allow-show-password");

        this.buttonShowHidePassword?.visible.set(type === TextBoxTypeEnum.password && change.currentValue);
        this.$$valueElement?.attr("type", this._getHtmlType() ?? null);
    }
    protected _autoCompleteChanged(change: PropertyChange<string>)
    {
        if (change.currentValue)
            this.$$valueElement?.attr("autocomplete", change.currentValue);
        else
            this.$$valueElement?.removeAttr("autocomplete");

        if (change.currentValue === "off")
            this.$$valueElement?.attr("role", "presentation");
        else
            this.$$valueElement?.removeAttr("role");
    }
    protected _changeEventChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _createInternalElements()
    {
        const
            $$element = this.$$element.get(),
            type = this.type.get(),
            maximumLength = this.maximumLength.get(),
            changeEvent = this.changeEvent.get(),
            htmlInputType = this._getHtmlType();

        let
            numberStep;

        Assert.isNotNullOrUndefined(changeEvent, "changeEvent");

        switch (type)
        {
            case TextBoxTypeEnum.number:
                numberStep = "1";
                break;
            case TextBoxTypeEnum.number2Dp:
                numberStep = "0.01";
                break;
        }

        $$element?.empty();

        if (this.$$valueElement)
        {
            this.$$valueElement.off("blur", { self: this }, this.onValueElementBlurred);
            this.$$valueElement.off(changeEvent, { self: this }, this.onValueElementChanged);
            this.$$valueElement.off("focus", { self: this }, this.onValueElementFocussed);
            this.$$valueElement.off("keyup", { self: this }, this.onValueElementKeyUp);
        }

        switch (type)
        {
            case TextBoxTypeEnum.multiline:
                // No left label
                break;
            default:
                this.$$leftLabelElement = JNativeStatic.create$$({
                    nodeName: "div",
                    attributes: { "class": "ui label" }
                }).hide().appendTo($$element);
                break;
        }

        this.$$iconElement = JNativeStatic.create$$({
            nodeName: "i",
            attributes: { "class": "icon" }
        }).hide().appendTo($$element);

        switch (type)
        {
            case TextBoxTypeEnum.multiline:
                this.$$valueElement = $$<HTMLTextAreaElement>(StringHelper.formatWithHtmlEncode("<textarea id=\"{0}-textarea\"></textarea>", this.controlId)).appendTo($$element);
                break;
            case TextBoxTypeEnum.number:
            case TextBoxTypeEnum.number2Dp:
                this.$$valueElement = $$<HTMLInputElement>(StringHelper.formatWithHtmlEncode("<input id=\"{0}-input\" type=\"{1}\" step=\"{2}\" />", this.controlId, htmlInputType, numberStep)).appendTo($$element);
                break;
            default:
                this.$$valueElement = $$<HTMLInputElement>(StringHelper.formatWithHtmlEncode("<input id=\"{0}-input\" type=\"{1}\" />", this.controlId, htmlInputType)).appendTo($$element);
                break;
        }

        this.$$valueElement.on("blur", { self: this }, this.onValueElementBlurred);
        this.$$valueElement.on(changeEvent, { self: this }, this.onValueElementChanged);
        this.$$valueElement.on("focus", { self: this }, this.onValueElementFocussed);
        this.$$valueElement.on("keyup", { self: this }, this.onValueElementKeyUp);

        switch (type)
        {
            case TextBoxTypeEnum.multiline:
                // No right label
                break;
            default:
                this.$$rightLabelElement = JNativeStatic.create$$({
                    nodeName: "div",
                    attributes: { "class": "ui label" }
                }).hide().appendTo($$element);
                break;
        }

        this.buttonShowHidePassword =
            new Button({ cssClass: "rad-action-show-hide-password", inline: true, svgKey: this.allowShowPassword.get() && this.showPassword.get() ? "hlo-icon-password-shown" : "hlo-icon-password-hidden" });

        this.buttonShowHidePassword.$$element.get()?.appendTo($$element);
        this.buttonShowHidePassword.hide();

        this.$$valueElement.attr("maxlength", maximumLength?.toString() ?? null);
        this.labelTargetId = this.$$valueElement.attr("id") ?? undefined;

        const
            isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;

        if (htmlInputType === "number" && isIOS)
            this.$$valueElement.attr("pattern", "\\d*");
        else
            this.$$valueElement.removeAttr("pattern");

        this._elementValueSet(this.value.get());
    }
    protected _elementValueChanged()
    {
        if (!this.__processElementValueChanged)
            return;

        this.value.set(this._elementValueGet());
    }
    protected _elementValueGet()
    {
        return this._formatValue(this.$$valueElement?.val());
    }
    protected _elementValueSet(value: any)
    {
        this.__processElementValueChanged = false;
        this.$$valueElement?.val(this._formatValue(value));
        this.__processElementValueChanged = true;
    }
    protected _formatValue(value: any)
    {
        if (ObjectHelper.isNullOrUndefined(value))
            return null;

        switch (this.type.get())
        {
            case TextBoxTypeEnum.number2Dp:
                {
                    const
                        valueFloat = parseFloat(value);

                    return !isNaN(valueFloat) ? valueFloat.toFixed(2) : null;
                }
            default:
                return value;
        }
    }
    protected _getHtmlType()
    {
        const
            type = this.type.get();

        switch (type)
        {
            case TextBoxTypeEnum.date:
                return "text";
            case TextBoxTypeEnum.email:
                return "email";
            case TextBoxTypeEnum.multiline:
                return StringHelper.empty;
            case TextBoxTypeEnum.normal:
                return "text";
            case TextBoxTypeEnum.number:
            case TextBoxTypeEnum.number2Dp:
                return "number";
            case TextBoxTypeEnum.password:
                return this.allowShowPassword.get() && this.showPassword.get() ? "text" : "password";
            default:
                console.error(StringHelper.format("Type \"{0}\" is not supported", type));
                break;
        }
    }
    protected _iconChanged(change: PropertyChange<string>)
    {
        if (change.originalValue)
        {
            this.removeCssClass("icon");
            this.$$iconElement?.removeClass(change.originalValue);
            this.$$iconElement?.hide();
        }

        if (change.currentValue)
        {
            this.addCssClass("icon");
            this.$$iconElement?.addClass(change.currentValue);
            this.$$iconElement?.show();
        }
    }
    protected _inputMaxChanged(change: PropertyChange<number>)
    {
        this.$$valueElement?.attr("max", change.currentValue?.toString() ?? null);
    }
    protected _inputMinChanged(change: PropertyChange<number>)
    {
        this.$$valueElement?.attr("min", change.currentValue?.toString() ?? null);
    }
    protected _inputSizeChanged(change: PropertyChange<number>)
    {
        this.$$valueElement?.attr("size", change.currentValue?.toString() ?? null);
    }
    protected _leftLabelTextChanged(change: PropertyChange<string>)
    {
        this._updateLayout();
    }
    protected _maximumLengthChanged(change: PropertyChange<number>)
    {
        this.$$valueElement?.attr("maxlength", change.currentValue?.toString() ?? null);
    }
    protected _placeholderChanged(change: PropertyChange<string>)
    {
        this.$$valueElement?.attr("placeholder", change.currentValue);
    }
    protected _rightLabelTextChanged(change: PropertyChange<string>)
    {
        this._updateLayout();
    }
    protected _showPasswordChanged(change: PropertyChange<boolean>)
    {
        const
            type = this.type.get();

        this.buttonShowHidePassword?.svgKey.set(type === TextBoxTypeEnum.password && this.allowShowPassword.get() && this.showPassword.get() ? "hlo-icon-password-shown" : "hlo-icon-password-hidden");
        this.$$valueElement?.attr("type", this._getHtmlType() ?? null);
    }
    protected _svgKeyChanged(change: PropertyChange<string>)
    {
        if (change.currentValue)
        {
            this.$$iconElement?.empty();
            this.$$iconElement?.append(SvgHelper.createSvg(change.currentValue)).show();
        }
        else
            this.$$iconElement?.hide();
    }
    protected _typeChanged(change: PropertyChange<TextBoxTypeEnum>)
    {
        this._createInternalElements();

        // Trigger Property Changes
        this.allowShowPassword.triggerChange();
        this.autoComplete.triggerChange();
        this.icon.triggerChange();
        this.inputMax.triggerChange();
        this.inputMin.triggerChange();
        this.inputSize.triggerChange();
        this.leftLabelText.triggerChange();
        this.placeholder.triggerChange();
        this.rightLabelText.triggerChange();
        this.showPassword.triggerChange();
        this.svgKey.triggerChange();
    }
    protected _updateLayout()
    {
        if (!(this.$$leftLabelElement && this.$$rightLabelElement))
            return;

        const
            leftLabelText = this.leftLabelText.get(),
            rightLabelText = this.rightLabelText.get();

        this.$$leftLabelElement.text(leftLabelText);
        this.$$rightLabelElement.text(rightLabelText);

        if (leftLabelText || rightLabelText)
        {
            if (rightLabelText)
                this.addCssClass("right labeled");
            else
                this.addCssClass("labeled");
        }
        else
            this.removeCssClass("right labeled");

        if (leftLabelText)
            this.$$leftLabelElement.show();
        else
            this.$$leftLabelElement.hide();

        if (rightLabelText)
            this.$$rightLabelElement.show();
        else
            this.$$rightLabelElement.hide();
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: TextBoxPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.allowShowPassword = new PropertyBoolean(this, propertyValues.allowShowPassword, this._allowShowPasswordChanged);
        this.autoComplete = new PropertyString(this, propertyValues.autoComplete, this._autoCompleteChanged);
        this.changeEvent = new PropertyString(this, propertyValues.changeEvent, this._changeEventChanged);
        this.icon = new PropertyString(this, propertyValues.icon, this._iconChanged);
        this.inputMax = new PropertyDecimal(this, propertyValues.inputMax, this._inputMaxChanged);
        this.inputMin = new PropertyDecimal(this, propertyValues.inputMin, this._inputMinChanged);
        this.inputSize = new PropertyInt32(this, propertyValues.inputSize, this._inputSizeChanged);
        this.leftLabelText = new PropertyString(this, propertyValues.leftLabelText, this._leftLabelTextChanged);
        this.maximumLength = new PropertyInt32(this, propertyValues.maximumLength, this._maximumLengthChanged);
        this.placeholder = new PropertyString({
            context: this,
            initialValue: propertyValues.placeholder,
            changeHandler: this._placeholderChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Appearance" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Placeholder" }),
            ],
        });
        this.rightLabelText = new PropertyString(this, propertyValues.rightLabelText, this._rightLabelTextChanged);
        this.showPassword = new PropertyBoolean(this, propertyValues.showPassword, this._showPasswordChanged);
        this.svgKey = new PropertyString(this, propertyValues.svgKey, this._svgKeyChanged);
        this.type = new Property<TextBoxTypeEnum>({
            context: this,
            dotNetType: "System.String, System.Private.CoreLib",
            initialValue: propertyValues.type,
            changeHandler: this._typeChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Behavior" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Type" }),
            ],
        });

        this.text.overrideGet(next =>
        {
            const
                value = this.value.get();

            return value ? `${this.value.get()}` : value;
        });
    }

    protected override _bindEvents()
    {
        super._bindEvents();
        this.on("keydown", { self: this }, this.onKeyDown);
        this.buttonShowHidePassword?.on("click", { self: this }, this.onButtonShowHidePasswordClicked);
    }
    protected override _createElement()
    {
        const
            $$element = super._createElement();

        switch (this.type.get())
        {
            case TextBoxTypeEnum.date:
            case TextBoxTypeEnum.email:
            case TextBoxTypeEnum.normal:
            case TextBoxTypeEnum.password:
                $$element.addClass("input");
                break;
            case TextBoxTypeEnum.multiline:
                $$element.addClass("form");
                break;
        }

        return $$element;
    }
    protected override _dispose()
    {
        this.buttonShowHidePassword?.remove();

        super._dispose();
    }
    protected override _enabledChanged(change: PropertyChange<boolean>)
    {
        super._enabledChanged(change);

        if (change.currentValue)
            this.$$valueElement?.removeAttr("disabled");
        else
            this.$$valueElement?.attr("disabled", "disabled");
    }
    public override _getFocusElement()
    {
        return this.$$valueElement ?? null;
    }
    protected override _readOnlyChanged(change: PropertyChange<boolean>)
    {
        super._readOnlyChanged(change);

        if (change.currentValue)
            this.addCssClass("read-only");
        else
            this.removeCssClass("read-only");

        this.$$valueElement?.prop("readOnly", change.currentValue);
    }
    protected override _valueChanged(change: PropertyChange<any>)
    {
        super._valueChanged(change);
        this._elementValueSet(change.currentValue);
    }

    // #endregion

    // #region Event Handlers

    protected onButtonShowHidePasswordClicked(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self.showPassword.set(!self.showPassword.get());
    }
    protected onKeyDown(evt: IJNativeEvent<KeyboardEvent>)
    {
        const
            self = evt.data.self,
            type = self.type.get();

        let
            preventDefault = false;

        if (type === TextBoxTypeEnum.number)
            preventDefault = ["+", "-", ".", "e"].indexOf(evt.originalEvent.key) >= 0;
        else if (type === TextBoxTypeEnum.number2Dp)
            preventDefault = ["+", "-", "e"].indexOf(evt.originalEvent.key) >= 0;

        if (preventDefault)
            evt.preventDefault();
    }
    protected onValueElementBlurred(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        if (self.type.get() === TextBoxTypeEnum.date)
        {
            self.$$valueElement?.attr("type", "text");

            const
                value = self.value.get();

            self.value.set(
                /[0-9]{4}-[0-9]{2}-[0-9]{2}/.test(value)
                    ? `${value.substring(8, 10)}-${value.substring(5, 7)}-${value.substring(0, 4)}`
                    : null
            );
        }
    }
    protected onValueElementChanged(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self._elementValueChanged();
    }
    protected onValueElementFocussed(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        if (self.type.get() === TextBoxTypeEnum.date)
        {
            const
                value = self.value.get();

            self.value.set(
                /[0-9]{2}-[0-9]{2}-[0-9]{4}/.test(value)
                    ? `${value.substring(6, 10)}-${value.substring(3, 5)}-${value.substring(0, 2)}`
                    : null
            );

            self.$$valueElement?.attr("type", "date");
        }
    }
    protected onValueElementKeyUp(evt: IJNativeEvent<KeyboardEvent>)
    {
        const
            self = evt.data.self as this;

        if (self.type.get() === TextBoxTypeEnum.multiline)
        {
            switch (evt.originalEvent.key)
            {
                case "Enter":
                    evt.stopPropagation();
                    break;
            }
        }
    }

    // #endregion

    // #endregion
}